import Select, { ActionMeta } from "react-select";
import { noop } from "lodash";

import ErrorMessage from "@components/form/ErrorMessage";
import { useAllLevel1Resources } from "@hooks/query/useAllLevel1Resources";

type OverrideProps<T, TOverridden> = Omit<T, keyof TOverridden> & TOverridden;

type Option = {
  label: string;
  value: string;
  identifier: string;
  level0WRSMembers: any;
};

type SelectLevel1ResourceProps = {
  className?: string;
  errorMessage?: string;
  onlySALevel1Resources?: boolean;
} & OverrideProps<
  React.ComponentProps<Select>,
  {
    value?: string;
    onChange?: (value: Option, actionMeta: ActionMeta<Option>) => void;
  }
>;

const SelectLevel1Resource: React.FunctionComponent<
  SelectLevel1ResourceProps
> = ({
  errorMessage,
  value,
  className,
  onlySALevel1Resources = false,
  onChange = noop,
  ...props
}) => {
  const { isLoading, data: options = [] } = useAllLevel1Resources({
    select: (data: Record<string, any>[]) => {
      const mapped = data
        .map(i => ({
          label: i.name,
          value: i.id,
          identifier: i.identifier,
          level0WRSMembers: i.level0WRSMembers,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      return onlySALevel1Resources
        ? mapped.filter(
            i =>
              i.level0WRSMembers.length > 0 &&
              i.level0WRSMembers.some(
                (member: { saApproval: boolean }) => member.saApproval,
              ),
          )
        : mapped;
    },
  });

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Select
        options={options}
        value={options.find((i: any) => i.value === value || i.label === value)}
        className={className}
        onChange={onChange}
        openMenuOnFocus
        {...props}
      />

      {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
    </>
  );
};

export default SelectLevel1Resource;
